import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, 
  Eye, 
  Wifi, 
  Code, 
  CheckCircle, 
  ArrowRight,
  Monitor,
  Shield,
  Zap,
  Database,
  Brain,
  Globe,
  Smartphone,
  Layers,
  Activity,
  Settings,
  Target,
  TrendingUp,
  Clock,
  Award,
  Star,
  Play,
  Download,
  ExternalLink
} from 'lucide-react';
import './SolutionsSection.css';

const SolutionsSection = () => {
  const [activeSolution, setActiveSolution] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const [activeDemo, setActiveDemo] = useState(null);
  const sectionRef = useRef(null);

  // Enhanced solutions data with more details
  const solutionsData = [
    {
      id: 'erp-education',
      icon: <Users size={40} />,
      title: 'ERP for Educational Institutions',
      shortTitle: 'Education ERP',
      description: 'Comprehensive management system designed specifically for schools and educational institutions with advanced analytics, automated workflows, and seamless integration capabilities.',
      features: [
        'Student Information Management',
        'Academic Progress Tracking', 
        'Fee Management & Billing',
        'Automated Report Generation',
        'Parent-Teacher Communication',
        'Examination & Assessment Tools'
      ],
      technologies: ['React', 'Node.js', 'PostgreSQL', 'Redis'],
      stats: {
        institutions: '23+',
        students: '10K+',
        uptime: '99.9%'
      },
      benefits: [
        'Reduce administrative workload by 70%',
        'Improve communication efficiency',
        'Real-time performance analytics',
        'Secure data management'
      ],
      demoVideo: '/demo/erp-demo.mp4',
      caseStudy: '/case-studies/greenwood-academy',
      color: 'blue',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'facial-recognition',
      icon: <Eye size={40} />,
      title: 'AI-Powered Facial Recognition',
      shortTitle: 'Face Recognition',
      description: 'Advanced AI-powered facial recognition system for secure and contactless attendance management with real-time processing, anti-spoofing technology, and comprehensive analytics.',
      features: [
        'Real-time Face Detection',
        'Advanced Anti-spoofing',
        'Multi-camera Support',
        'Cloud-based Analytics',
        'Mobile App Integration',
        'Attendance Automation'
      ],
      technologies: ['Python', 'TensorFlow', 'OpenCV', 'AWS'],
      stats: {
        accuracy: '99.95%',
        speed: '<1s',
        capacity: '10K faces'
      },
      benefits: [
        'Contactless attendance marking',
        'Eliminate buddy punching',
        'Real-time attendance tracking',
        'Advanced security features'
      ],
      demoVideo: '/demo/facial-demo.mp4',
      caseStudy: '/case-studies/techcorp-security',
      color: 'purple',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      id: 'rfid-attendance',
      icon: <Wifi size={40} />,
      title: 'Advanced RFID Systems',
      shortTitle: 'RFID Solutions',
      description: 'Professional RFID-based attendance machines with industry-leading range, offline storage capabilities, battery backup, and seamless integration with existing infrastructure.',
      features: [
        '8-12 Meter Reading Range',
        'Offline Data Storage',
        '48-Hour Battery Backup',
        'Multi-card Support',
        'Weather-resistant Design',
        'Auto-sync Capabilities'
      ],
      technologies: ['C++', 'Embedded Systems', 'IoT', 'MySQL'],
      stats: {
        range: '12m',
        battery: '48hrs',
        storage: '100K records'
      },
      benefits: [
        'Extended reading range',
        'Uninterrupted operation',
        'Weatherproof design',
        'Easy installation'
      ],
      demoVideo: '/demo/rfid-demo.mp4',
      caseStudy: '/case-studies/manufacturing-facility',
      color: 'green',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      id: 'custom-development',
      icon: <Code size={40} />,
      title: 'Custom Software Development',
      shortTitle: 'Custom Solutions',
      description: 'Cross-platform development services for Windows, Web, Android, iOS, Linux, and Mac platforms with modern architecture, scalable design, and comprehensive support.',
      features: [
        'Cross-platform Development',
        'Scalable Architecture',
        'Modern UI/UX Design',
        'Cloud Integration',
        'API Development',
        'Ongoing Maintenance'
      ],
      technologies: ['React', 'Flutter', '.NET', 'Docker'],
      stats: {
        platforms: '6+',
        projects: '150+',
        satisfaction: '98%'
      },
      benefits: [
        'Unified codebase for all platforms',
        'Faster time-to-market',
        'Reduced development costs',
        'Professional support'
      ],
      demoVideo: '/demo/custom-demo.mp4',
      caseStudy: '/case-studies/multi-platform-app',
      color: 'orange',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  // Platform icons for custom development
  const platforms = [
    { name: 'Windows', icon: <Monitor size={20} />, color: '#0078D4' },
    { name: 'Web', icon: <Globe size={20} />, color: '#61DAFB' },
    { name: 'Android', icon: <Smartphone size={20} />, color: '#3DDC84' },
    { name: 'iOS', icon: <Smartphone size={20} />, color: '#007AFF' },
    { name: 'Linux', icon: <Settings size={20} />, color: '#FCC624' },
    { name: 'Mac', icon: <Monitor size={20} />, color: '#000000' }
  ];

  // Industry statistics
  const industryStats = [
    { label: 'Institutions Served', value: '23+', icon: <Target size={24} /> },
    { label: 'Active Users', value: '10K+', icon: <Users size={24} /> },
    { label: 'System Uptime', value: '99.9%', icon: <TrendingUp size={24} /> },
    { label: 'Customer Satisfaction', value: '98%', icon: <Star size={24} /> }
  ];

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardIndex = parseInt(entry.target.dataset.index);
            setVisibleCards(prev => [...new Set([...prev, cardIndex])]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const cards = sectionRef.current?.querySelectorAll('[data-index]');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  // Auto-cycle through solutions
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSolution((prev) => (prev + 1) % solutionsData.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleCardHover = (index) => {
    setHoveredCard(index);
  };

  const handleCardLeave = () => {
    setHoveredCard(null);
  };

  const handleLearnMore = (solutionId) => {
    console.log(`Learn more about: ${solutionId}`);
    // Add navigation logic here
  };

  const handleDemo = (solutionId) => {
    setActiveDemo(solutionId);
    console.log(`Show demo for: ${solutionId}`);
    // Add demo modal logic here
  };

  const handleDownload = (solutionId) => {
    console.log(`Download brochure for: ${solutionId}`);
    // Add download logic here
  };

  return (
    <section id="solutions" className="solutions" ref={sectionRef}>
      {/* Animated Background */}
      <div className="solutions__background">
        <div className="solutions__particles">
          {Array.from({ length: 15 }).map((_, i) => (
            <div
              key={i}
              className="solutions__particle"
              style={{
                '--delay': `${i * 0.3}s`,
                '--duration': `${4 + (i % 3)}s`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`
              }}
            />
          ))}
        </div>
      </div>

      <div className="solutions__container">
        {/* Enhanced Header */}
        <div className="solutions__header">
          <div className="solutions__badge">
            <Layers className="solutions__badge-icon" />
            <span>Our Solutions Portfolio</span>
          </div>
          <h2 className="solutions__title">
            <span className="solutions__title-line">Comprehensive Technology</span>
            <span className="solutions__title-gradient">Solutions</span>
          </h2>
          <p className="solutions__subtitle">
            Comprehensive technology solutions designed to streamline operations, 
            enhance productivity, and drive digital transformation for educational 
            institutions and modern businesses.
          </p>
        </div>

        {/* Industry Stats */}
        <div className="solutions__stats">
          <div className="solutions__stats-grid">
            {industryStats.map((stat, index) => (
              <div key={index} className="solutions__stat">
                <div className="solutions__stat-icon">
                  {stat.icon}
                </div>
                <div className="solutions__stat-content">
                  <div className="solutions__stat-value">{stat.value}</div>
                  <div className="solutions__stat-label">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Solutions Grid */}
        <div className="solutions__grid">
          {solutionsData.map((solution, index) => (
            <div 
              key={solution.id} 
              className={`solutions__card solutions__card--${solution.color} ${
                visibleCards.includes(index) ? 'solutions__card--visible' : ''
              } ${hoveredCard === index ? 'solutions__card--hovered' : ''}`}
              data-index={index}
              onMouseEnter={() => handleCardHover(index)}
              onMouseLeave={handleCardLeave}
            >
              {/* Card Header */}
              <div className="solutions__card-header">
                <div className="solutions__card-icon">
                  {solution.icon}
                  <div className="solutions__card-icon-glow"></div>
                </div>
                <div className="solutions__card-title-section">
                  <h3 className="solutions__card-title">{solution.title}</h3>
                  <div className="solutions__card-tech-stack">
                    {solution.technologies.slice(0, 3).map((tech, idx) => (
                      <span key={idx} className="solutions__tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Card Content */}
              <div className="solutions__card-content">
                <p className="solutions__card-description">
                  {solution.description}
                </p>
                
                {/* Key Features */}
                <div className="solutions__card-features">
                  <h4 className="solutions__features-title">Key Features</h4>
                  <div className="solutions__features-grid">
                    {solution.features.slice(0, 4).map((feature, idx) => (
                      <div key={idx} className="solutions__card-feature">
                        <CheckCircle className="solutions__card-feature-icon" />
                        <span className="solutions__card-feature-text">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Statistics */}
                <div className="solutions__card-stats">
                  {Object.entries(solution.stats).map(([key, value], idx) => (
                    <div key={idx} className="solutions__card-stat">
                      <div className="solutions__card-stat-value">{value}</div>
                      <div className="solutions__card-stat-label">
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Benefits Preview */}
                <div className="solutions__card-benefits">
                  <h4 className="solutions__benefits-title">Key Benefits</h4>
                  <ul className="solutions__benefits-list">
                    {solution.benefits.slice(0, 2).map((benefit, idx) => (
                      <li key={idx} className="solutions__benefit-item">
                        <Award size={14} />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Card Actions */}
              <div className="solutions__card-actions">
                <button 
                  className="solutions__card-button solutions__card-button--primary"
                  onClick={() => handleLearnMore(solution.id)}
                >
                  <span>Learn More</span>
                  <ArrowRight className="solutions__card-button-icon" />
                </button>
                <div className="solutions__card-secondary-actions">
                  <button 
                    className="solutions__card-action"
                    onClick={() => handleDemo(solution.id)}
                    title="Watch Demo"
                  >
                    <Play size={16} />
                  </button>
                  <button 
                    className="solutions__card-action"
                    onClick={() => handleDownload(solution.id)}
                    title="Download Brochure"
                  >
                    <Download size={16} />
                  </button>
                  <button 
                    className="solutions__card-action"
                    title="Case Study"
                  >
                    <ExternalLink size={16} />
                  </button>
                </div>
              </div>

              {/* Custom Development Platforms */}
              {solution.id === 'custom-development' && (
                <div className="solutions__platforms">
                  <h4 className="solutions__platforms-title">Supported Platforms</h4>
                  <div className="solutions__platforms-grid">
                    {platforms.map((platform, idx) => (
                      <div 
                        key={idx} 
                        className="solutions__platform"
                        style={{ '--platform-color': platform.color }}
                      >
                        {platform.icon}
                        <span>{platform.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hover Overlay */}
              <div className="solutions__card-overlay">
                <div className="solutions__overlay-content">
                  <h4 className="solutions__overlay-title">
                    Ready to get started with {solution.shortTitle}?
                  </h4>
                  <p className="solutions__overlay-description">
                    Contact our experts for a personalized demonstration and 
                    consultation tailored to your specific needs.
                  </p>
                  <button className="solutions__overlay-cta">
                    <span>Get Started</span>
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>

              {/* Card Progress Indicator */}
              <div className="solutions__card-progress">
                <div 
                  className="solutions__progress-bar"
                  style={{ '--progress': `${((index + 1) / solutionsData.length) * 100}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Solution Navigator */}
        <div className="solutions__navigator">
          <div className="solutions__navigator-header">
            <h3 className="solutions__navigator-title">Solution Navigator</h3>
            <p className="solutions__navigator-subtitle">
              Explore our comprehensive suite of technology solutions
            </p>
          </div>
          <div className="solutions__navigator-tabs">
            {solutionsData.map((solution, index) => (
              <div 
                key={solution.id}
                className={`solutions__navigator-tab ${
                  activeSolution === index ? 'solutions__navigator-tab--active' : ''
                }`}
                onClick={() => setActiveSolution(index)}
              >
                <div className="solutions__tab-icon">
                  {solution.icon}
                </div>
                <div className="solutions__tab-content">
                  <h4 className="solutions__tab-title">{solution.shortTitle}</h4>
                  <p className="solutions__tab-description">
                    {solution.description.slice(0, 80)}...
                  </p>
                </div>
                <div className="solutions__tab-indicator"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="solutions__cta">
          <div className="solutions__cta-content">
            <div className="solutions__cta-text">
              <h3 className="solutions__cta-title">
                Ready to Transform Your Operations?
              </h3>
              <p className="solutions__cta-description">
                Join hundreds of organizations that trust our technology solutions. 
                Let's discuss how we can help you achieve your digital transformation goals.
              </p>
            </div>
            <div className="solutions__cta-actions">
              <button className="solutions__cta-primary">
                <span>Schedule Consultation</span>
                <ArrowRight size={20} />
              </button>
              <button className="solutions__cta-secondary">
                <span>View All Solutions</span>
                <ExternalLink size={18} />
              </button>
            </div>
          </div>
          <div className="solutions__cta-visual">
            <div className="solutions__cta-stats">
              <div className="solutions__cta-stat">
                <Clock size={32} />
                <div className="solutions__cta-stat-info">
                  <span className="solutions__cta-stat-number">24/7</span>
                  <span className="solutions__cta-stat-label">Support</span>
                </div>
              </div>
              <div className="solutions__cta-stat">
                <Shield size={32} />
                <div className="solutions__cta-stat-info">
                  <span className="solutions__cta-stat-number">100%</span>
                  <span className="solutions__cta-stat-label">Secure</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;